/**
 * 依恋型人格你的依恋风格计算工具
 */

/**
 * 计算你的依恋风格
 * @param {Object} userAnswers 用户回答，键为题目序号(currentQuestion)，值为用户选择的选项
 * @param {Object} testData 测试数据，包含题目和计分规则
 * @returns {Object} 计算结果，包含焦虑和回避两个维度的得分及依恋类型
 */
function calculateResults(userAnswers, testData) {
  const questions = testData.questions;
  const reverseItems = testData.scoring.reverse_scoring.items;

  // 初始化维度得分
  let anxietyScore = 0;
  let avoidanceScore = 0;
  let anxietyCount = 0;
  let avoidanceCount = 0;

  // 计算每个维度的分数
  questions.forEach((question, index) => {
    const questionId = question.id;
    // 使用题目序号(index+1)而不是ID来获取用户回答
    const answer = userAnswers[index + 1];

    if (!answer) return;

    // 判断是否需要反向计分
    let score = answer;
    if (reverseItems.includes(questionId)) {
      score = 8 - answer; // 7点量表的反向计分: 1→7, 2→6, ...
    }

    // 根据问题维度累加分数
    if (question.dimension === 'Anxiety') {
      anxietyScore += score;
      anxietyCount++;
    } else if (question.dimension === 'Avoidance') {
      avoidanceScore += score;
      avoidanceCount++;
    }
  });

  // 计算平均分
  const anxietyAvg = anxietyCount > 0 ? anxietyScore / anxietyCount : 0;
  const avoidanceAvg = avoidanceCount > 0 ? avoidanceScore / avoidanceCount : 0;

  // 确定依恋类型
  const attachmentStyle = determineAttachmentStyle(anxietyAvg, avoidanceAvg);

  return {
    anxiety: {
      score: anxietyAvg.toFixed(2),
      level: determineLevel(anxietyAvg)
    },
    avoidance: {
      score: avoidanceAvg.toFixed(2),
      level: determineLevel(avoidanceAvg)
    },
    attachmentStyle
  };
}

/**
 * 确定得分等级
 * @param {Number} score 得分
 * @returns {String} 等级：Low, Average, High
 */
function determineLevel(score) {
  // 基于ECR-S量表标准微调阈值
  // 让大多数人感觉"差不多"是对的，确保合理分布
  if (score < 3) {
    return 'Low';      // 明显偏低
  } else if (score <= 5) {
    return 'Average';  // 大多数人范围
  } else {
    return 'High';     // 明显偏高
  }
}

/**
 * 确定依恋风格
 * @param {Number} anxietyScore 焦虑得分
 * @param {Number} avoidanceScore 回避得分
 * @returns {String} 依恋风格：Secure, Preoccupied, Dismissing, Fearful
 */
function determineAttachmentStyle(anxietyScore, avoidanceScore) {
  const anxietyLevel = determineLevel(anxietyScore);
  const avoidanceLevel = determineLevel(avoidanceScore);

  if (anxietyLevel === 'Low' && avoidanceLevel === 'Low') {
    return 'Secure'; // 安全型
  } else if (anxietyLevel === 'High' && (avoidanceLevel === 'Low' || avoidanceLevel === 'Average')) {
    return 'Preoccupied'; // 焦虑型
  } else if ((anxietyLevel === 'Low' || anxietyLevel === 'Average') && avoidanceLevel === 'High') {
    return 'Dismissing'; // 回避型
  } else if (anxietyLevel === 'High' && avoidanceLevel === 'High') {
    return 'Fearful'; // 恐惧型
  } else {
    // 不那么明确的情况，根据得分更接近哪种类型来判断
    const anxietyThreshold = 4; // (3 + 5) / 2，新的阈值中间值
    const avoidanceThreshold = 4;

    if (anxietyScore < anxietyThreshold && avoidanceScore < avoidanceThreshold) {
      return 'Secure';
    } else if (anxietyScore >= anxietyThreshold && avoidanceScore < avoidanceThreshold) {
      return 'Preoccupied';
    } else if (anxietyScore < anxietyThreshold && avoidanceScore >= avoidanceThreshold) {
      return 'Dismissing';
    } else {
      return 'Fearful';
    }
  }
}

export { calculateResults, determineLevel, determineAttachmentStyle };
